"use client"

import NextLink, { useLinkStatus } from "next/link"
import { type ComponentProps, useEffect } from "react"

import { useNavigationPending } from "@/lib/navigation/NavigationPendingContext"

type LinkProps = ComponentProps<typeof NextLink>

/**
 * Canonical in-app navigation wrapper. Thin pass-through over `next/link` —
 * the seam for app-wide concerns (analytics, prefetch policy, route gating).
 *
 * Side effect: each Link automatically reports its `useLinkStatus().pending`
 * to the surrounding `NavigationPendingProvider`, so the top router progress
 * bar and sidebar active-state can react to in-flight navigations without
 * each call site wiring it up.
 *
 * No visual styling. Consumers (or the wrapping primitive used via `asChild`)
 * own focus rings, border-radius, color, and underline. For external
 * (`http(s)://`) links use `components/common/ExternalLink` instead — it
 * forces `target="_blank"` and the safe `rel`.
 */
export const Link = ({ children, ...rest }: LinkProps) => (
  <NextLink {...rest}>
    <LinkPendingReporter />
    {children}
  </NextLink>
)

// Must be a descendant of `<NextLink>` for `useLinkStatus` to read this Link's
// transition state. Does two jobs while pending:
//  1. ref-counts into `NavigationPendingProvider` so the global progress bar
//     and sidebar active-state can observe in-flight transitions
//  2. renders a hidden `data-link-pending` marker so a parent can light up
//     via Tailwind's `has-[[data-link-pending]]:` (used by sidebar items)
const LinkPendingReporter = () => {
  const { pending } = useLinkStatus()
  const { registerPending } = useNavigationPending()
  useEffect(() => {
    if (!pending) return
    return registerPending()
  }, [pending, registerPending])
  return pending ? <span hidden data-link-pending /> : null
}
